/**
 * The Allow Energy listing card, drawn rather than skinned.
 *
 * <p>Anatomy and metrics are the approved mockup's
 * (`solar/docs/design/redesign-2026-10-01/catalogue.html`, with `css/tokens.css` and
 * `css/shared.css`): the `.card` stage over a hairline, the brand eyebrow, the mono MPN line,
 * the `.spec-table` microtable, the ruled `.price-row` with its pence-per-watt, the
 * `.doclinks` line, and the `.actions` row with the `.qty` stepper beside the owner-chosen
 * one-line Add (variant C, `css/variants.html`, 2026-10-01).
 *
 * <p><strong>Why this is a stylesheet of its own and not more rules in `catalogueCss.ts`.</strong>
 * That file styles commerce's grid from outside, through the `sf-card*` hooks it carries for a
 * theme. This grid is ours — the markup is in {@link SolarListingGrid} — so its rules hang off
 * its own class names and do not have to out-specify anybody's utilities. `catalogueCss.ts`
 * still owns the page round it (headings, filters, toolbar, pager) and still styles the
 * platform grid wherever a route other than the two listing routes renders one.
 *
 * <p>Every colour is a token. The `--ae-*` scope is declared in `TradeStyles.BASE_CSS` from the
 * tenant's palette, so both Horizon moods — the dark default and the Day palette held for later
 * — recolour this card without touching this file. The literals that remain are geometry: the
 * mockup's 4px spacing ladder, its 44px touch targets and its two breakpoints.
 */
export const LISTING_GRID_CSS = `
/* The grid itself. Intrinsic widths, so the cards respond to the space beside the filter
   rail rather than to the viewport — the listing's rail is 260px and a viewport-only rule
   puts four cramped cards where three fit. */
.ae-plp-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(100%,256px),1fr));
  gap:clamp(16px,2vw,24px);list-style:none;margin:0;padding:0}

/* ── The card ─────────────────────────────────────────────────────────────── */
.ae-plp-card{display:flex;flex-direction:column;min-width:0;overflow:hidden;
  border:1px solid var(--ae-border);border-radius:var(--ae-radius-lg);background:var(--ae-surface);
  box-shadow:var(--ae-shadow-sm);transition:border-color .18s ease,box-shadow .18s ease,transform .18s ease}
.ae-plp-card:hover{border-color:var(--ae-border-strong);box-shadow:var(--ae-shadow)}
@media (prefers-reduced-motion:no-preference){
  .ae-plp-card:hover{transform:translateY(-3px)}
}

/* The photograph on its own stage: a fixed pane over a hairline, the part contained and
   never cropped — a panel photographed edge to edge is the product, not a mood. */
.ae-plp-card__media{display:grid;place-items:center;height:200px;padding:24px;
  border-bottom:1px solid var(--ae-border);background:var(--ae-surface)}
.ae-plp-card__media img{max-height:160px;max-width:100%;width:auto;object-fit:contain}
.ae-plp-card__media--empty{color:var(--ae-muted);background:var(--ae-subtle);font-size:.8125rem;
  text-align:center;padding-inline:16px}

.ae-plp-card__body{display:flex;flex:1;flex-direction:column;gap:6px;min-width:0;padding:16px}
.ae-plp-card__brand{margin:0;font-size:.75rem;font-weight:650;letter-spacing:.08em;
  text-transform:uppercase;color:var(--ae-muted)}
.ae-plp-card__title{margin:0;font-size:1.0625rem;line-height:1.35;font-weight:640;
  letter-spacing:-.01em;overflow-wrap:anywhere}
.ae-plp-card__title a{color:var(--ae-ink);text-decoration:none}
.ae-plp-card__title a:hover{color:var(--ae-link);text-decoration:underline;text-underline-offset:.2em}

/* The part number a trade buyer arrived knowing, in the mono register voice. */
.ae-plp-card__mpn{margin:0;font-family:var(--font-family-mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace);
  font-size:.8125rem;font-weight:450;line-height:1.4;color:var(--ae-muted);overflow-wrap:anywhere}
.ae-plp-card__mpn abbr{text-decoration:none;letter-spacing:.04em}
.ae-plp-card__mpn-value{margin-left:8px;color:var(--ae-ink)}

/* ── The spec microtable: three hairline rows, figures in tabular numerals ── */
.ae-plp-card__specs{margin:8px 0 0;border-top:1px solid var(--ae-border)}
.ae-plp-card__srow{display:flex;justify-content:space-between;gap:12px;padding:5px 0;
  border-bottom:1px solid var(--ae-border);font-size:.8125rem}
.ae-plp-card__srow dt{color:var(--ae-muted);min-width:0;overflow-wrap:anywhere}
.ae-plp-card__srow dd{margin:0;text-align:right;font-weight:600;color:var(--ae-ink);
  font-variant-numeric:tabular-nums;overflow-wrap:anywhere}

/* ── Price, and the pence per watt it implies ─────────────────────────────── */
.ae-plp-card__price{margin-top:auto;padding-top:12px;border-top:1px solid var(--ae-border)}
.ae-plp-card__price-line{display:flex;align-items:baseline;flex-wrap:wrap;gap:6px;margin:0}
.ae-plp-card__now{font-size:1.375rem;line-height:1.2;font-weight:700;letter-spacing:-.01em;
  color:var(--ae-ink);font-variant-numeric:tabular-nums}
.ae-plp-card__was{font-size:.8125rem;color:var(--ae-muted);text-decoration:line-through;
  font-variant-numeric:tabular-nums}
.ae-plp-card__basis{font-size:.8125rem;font-weight:500;color:var(--ae-muted)}
.ae-plp-card__alt{margin:2px 0 0;font-size:.8125rem;color:var(--ae-muted);font-variant-numeric:tabular-nums}
.ae-plp-card__ppw{margin:4px 0 0;font-size:.8125rem;color:var(--ae-muted);font-variant-numeric:tabular-nums}
.ae-plp-card__ppw-now{font-weight:650;color:var(--ae-ink)}
.ae-plp-card__ppw-alt::before{content:" · "}
/* Never an empty space where a price goes, and never an invented figure. */
.ae-plp-card__poa{margin:0;font-size:.9375rem;font-weight:600;color:var(--ae-ink)}

.ae-plp-card__stock{margin:8px 0 0;font-size:.8125rem;color:var(--ae-muted)}
.ae-plp-card__stock[data-availability="IN_STOCK"]{color:var(--ae-success)}

/* The datasheet line keeps its height on a card that has no document, so a row of cards
   does not step up and down by one line depending on what the catalogue happens to hold. */
.ae-plp-card__docs{margin:8px 0 0;min-height:1.125rem;
  font-family:var(--font-family-mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace);
  font-size:.75rem;letter-spacing:.02em}
.ae-plp-card__docs a{color:var(--ae-link)}
.ae-plp-card__docs a:hover{color:var(--ae-link-hover)}

/* ── Buying from the card ─────────────────────────────────────────────────── */
.ae-plp-card__actions{display:flex;gap:8px;padding:12px 16px 16px}
.ae-plp-card__qty{display:inline-flex;flex:none;align-items:stretch;min-height:44px;overflow:hidden;
  border:1px solid var(--ae-border-strong);border-radius:var(--ae-radius);background:var(--ae-surface)}
.ae-plp-card__qty button{width:44px;border:0;background:var(--ae-subtle);color:var(--ae-ink);
  font-size:1rem;font-weight:600;cursor:pointer}
.ae-plp-card__qty button:hover:not(:disabled){background:var(--ae-border)}
.ae-plp-card__qty button:disabled{cursor:default;opacity:.5}
.ae-plp-card__qty input{width:40px;min-width:0;border:0;border-left:1px solid var(--ae-border);
  border-right:1px solid var(--ae-border);background:var(--ae-surface);color:var(--ae-ink);
  text-align:center;font-size:.9375rem;font-weight:600;font-variant-numeric:tabular-nums}
.ae-plp-card__qty input:focus{outline:2px solid var(--ae-focus);outline-offset:-2px}

/* The card's Add is the navy secondary voice — amber is reserved for a page's one primary
   CTA (DESIGN.md §5), which on these routes is the product page's buy button. */
.ae-plp-card__add{display:inline-flex;flex:1;align-items:center;justify-content:center;gap:8px;
  min-height:44px;padding:10px 12px;border:0;border-radius:var(--ae-radius);cursor:pointer;
  background:var(--ae-brand);color:var(--ae-on-brand);font-size:.9375rem;font-weight:600;white-space:nowrap}
.ae-plp-card__add:hover:not(:disabled){background:var(--ae-brand-deep)}
.ae-plp-card__add:disabled{cursor:default;opacity:.55}
.ae-plp-card__status{margin:0;padding:0 16px 12px;font-size:.8125rem;color:var(--ae-muted)}
.ae-plp-card__status:empty{display:none}
.ae-plp-card__status a{color:var(--ae-link)}
.ae-plp-card__status[data-state="failed"]{color:var(--ae-error)}

/* ── Nothing matched ──────────────────────────────────────────────────────── */
.ae-plp-empty{padding:32px 16px;border:1px solid var(--ae-border);border-radius:var(--ae-radius-lg);
  background:var(--ae-surface);text-align:center}
.ae-plp-empty p{margin:0;color:var(--ae-ink);font-weight:600}
.ae-plp-empty p + p{margin-top:4px;font-weight:400;color:var(--ae-muted)}

.ae-plp-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;
  clip-path:inset(50%);white-space:nowrap;border:0}
`;
